import { CdkAccordionItem, CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider, MatListItem, MatNavList } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { User } from '@tgbot/app/auth/models/user.model';
import { AuthService } from '@tgbot/app/auth/services/auth.service';
import { AvatarComponent } from '@tgbot/app/components/avatar/avatar.component';
import { FeatureFlag } from '@tgbot/app/constants/feature-flags.constant';
import { ROUTE_PATHS } from '@tgbot/app/constants/route-paths.constant';
import { HasGroupDirective } from '@tgbot/app/directives/group.directive';
import { RouteLink } from '@tgbot/app/models/route-link.model';
import { FullNamePipe } from '@tgbot/app/pipes/full-name.pipe';
import { environment } from 'environments/environment';

import { SideNavDropdownPanelDirective } from '../side-nav-dropdown/directives/side-nav-dropdown-panel.directive';
import { SideNavDropdownTriggerDirective } from '../side-nav-dropdown/directives/side-nav-dropdown-trigger.directive';
import { SideNavDropdownComponent } from '../side-nav-dropdown/side-nav-dropdown.component';

@Component({
  selector: 'crm-tgbot-side-nav',
  imports: [
    MatNavList,
    MatListItem,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    MatButtonModule,
    NgClass,
    MatDivider,
    FullNamePipe,
    AvatarComponent,
    CdkAccordionModule,
    SideNavDropdownComponent,
    SideNavDropdownTriggerDirective,
    SideNavDropdownPanelDirective,
    MatTooltipModule,
    CommonModule,
    HasGroupDirective,
  ],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  expanded = signal<boolean>(false);
  user = signal<User | null>(null);

  collapsed = new Set<string>();
  routeLinks: RouteLink[] = this.filterRouteLinks(ROUTE_PATHS);

  constructor() {
    this.user.set(this.authService.user());
  }
  toggleDrawer(): void {
    this.expanded.set(!this.expanded());
  }

  onHeaderClick(acc: CdkAccordionItem, rla: RouterLinkActive, id: string, ev: MouseEvent) {
    ev.stopPropagation();

    if (rla.isActive) {
      if (this.collapsed.has(id)) {
        this.collapsed.delete(id);
        acc.open();
      } else {
        this.collapsed.add(id);
        acc.close();
      }
    } else {
      this.collapsed.delete(id);
      acc.toggle();
    }
  }

  private filterRouteLinks(routes: RouteLink[]): RouteLink[] {
    return routes
      .map((route) => ({
        ...route,
        subRoutes: route.subRoutes ? this.filterRouteLinks(route.subRoutes) : undefined,
      }))
      .filter((route) => {
        if (route.featureFlag && !environment.featureFlags?.[route.featureFlag as FeatureFlag]) {
          return false;
        }

        return true;
      });
  }

  logout(): void {
    this.authService.beginLogout();
    this.router.navigate(['/login']).then((ok) => {
      if (!ok) {
        this.authService.cancelLogout();
        return;
      }
      this.authService.logout();
    });
  }
}
